import { Badge } from 'framework7-react';
import { FC, PropsWithChildren, ReactElement, ReactNode, useMemo } from 'react';
import { Button as BootstrapButton, ButtonProps as BootstrapButtonProps } from 'react-bootstrap';

import { useAnalytics } from '@hooks/useAnalytics';

import './Button.scss';

export interface ButtonProps extends Omit<PropsWithChildren<BootstrapButtonProps>, 'size'> {
  variant?: 'default' | 'flat' | 'outlined' | 'outlined dashed' | 'smooth';
  addBorder?: boolean;
  roundness?: 'rounded' | 'pill' | 'circle';
  color?:
    | 'primary'
    | 'secondary'
    | 'neutral'
    | 'danger'
    | 'success'
    | 'default'
    | 'green'
    | 'pink'
    | 'teal'
    | 'yellow';
  size?: 'sm' | 'md' | 'lg';
  track?: {
    event: string;
    properties?: {
      [key: string]: any;
    };
  };
  noWrap?: boolean;
  startIcon?: ReactElement | ReactNode;
  startIconAlign?: 'start' | 'center' | 'end';
  endIcon?: ReactElement | ReactNode;
  showHighlight?: boolean;
  contentElement?: ReactElement | ReactNode;
  chip?: {
    text: string | number;
  };
  // disabled: boolean;
}
const Button: FC<ButtonProps> = ({
  className = '',
  variant = 'default',
  addBorder,
  roundness = 'pill',
  color = 'primary',
  size = 'md',
  startIcon,
  startIconAlign,
  endIcon,
  noWrap,
  children,
  track: trackProperties,
  onClick,
  showHighlight,
  contentElement,
  chip,
  ...rest
}) => {
  const classNamesAppended = `btn ${className} btn-variant-${variant} btn-roundness-${roundness} btn-color-${color} btn-size-${size} ${noWrap ? 'text-nowrap' : ''} ${showHighlight ? 'show-highlight' : ''} ${addBorder ? 'add-border' : ''}`;
  const { track } = useAnalytics();

  const handleClick = (e) => {
    if (trackProperties) {
      track(trackProperties.event, trackProperties.properties);
    }

    onClick?.(e);
  };

  const endIconContent = useMemo(() => {
    if (endIcon) {
      return endIcon;
    }

    if (chip) {
      return (
        <div style={{ height: 20, marginTop: -10, marginBottom: -10 }}>
          <Badge className="px-2" color="red">
            {chip.text}
          </Badge>
        </div>
      );
    }

    return undefined;
  }, [chip, endIcon]);

  return (
    <BootstrapButton className={classNamesAppended} {...rest} onClick={handleClick}>
      {contentElement ?? (
        <span className="d-flex align-items-center justify-content-center">
          {startIcon && (
            <div
              className={`btn_start-icon ${startIconAlign ? `align-self-${startIconAlign}` : ''}`}
            >
              {startIcon}
            </div>
          )}
          <span>{children}</span>
          {endIconContent && <div className="btn_end-icon">{endIconContent}</div>}
        </span>
      )}
    </BootstrapButton>
  );
};

export default Button;
